import { useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { SectionCard } from "shared/app/SectionCard";
import { Select, Button, ButtonVariant, ButtonSize } from "shared/components";
import type { BaseOption } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { useInboxToken } from "../../inbox/api/useInboxToken";
import {
  usePhoneNumbers,
  usePropertyPhoneDefault,
  useSetPropertyPhoneDefault
} from "../../phone-numbers/lib/hooks";
import {
  useCommunicationDefaultEmail,
  useSetCommunicationDefaultEmail
} from "../lib/communicationDefaults";
import { formatPhone } from "shared/utils/format-phone";

const PhoneNumberControl = ({
  propertyId,
  activeNumbers,
  selectedValue,
  options,
  noNumbersAssigned,
  onChange
}: {
  propertyId: number;
  activeNumbers: { communication_service_id: string }[];
  selectedValue: string;
  options: BaseOption[];
  noNumbersAssigned: boolean;
  onChange: (e: { target: { value: string } }) => void;
}) => {
  if (activeNumbers.length > 0)
    return (
      <Select
        id={`property-${propertyId}-phone-default`}
        hideOptional
        value={selectedValue}
        onChange={onChange}
        options={options}
        legacyErrorMargins={false}
        className="max-w-[280px]"
      />
    );

  if (noNumbersAssigned)
    return (
      <div className="space-y-2">
        <p className="text-sm text-content-400">
          No phone numbers are assigned to this property.
        </p>
        <Link to="/phone-numbers">
          <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
            Manage phone numbers
          </Button>
        </Link>
      </div>
    );

  return (
    <Link to="/settings/communication">
      <Button variant={ButtonVariant.BASE} size={ButtonSize.SM}>
        Set up phone numbers
      </Button>
    </Link>
  );
};

const EmailDefaultControl = () => {
  const { type } = useAuthContext();
  const { data, isError } = useCommunicationDefaultEmail();
  const { mutate: setEmailDefault, isPending } =
    useSetCommunicationDefaultEmail();

  const options: BaseOption[] = useMemo(
    () =>
      (data?.available_emails ?? []).map((email) => ({
        value: email,
        label: email
      })),
    [data]
  );

  // Hide the control when the partner lacks the communication feature (GET
  // errors) or has no selectable emails to choose from.
  if (isError || !data) return null;
  const {
    available_emails,
    communication_default_email,
    resolved_communication_default_email
  } = data;
  if (available_emails.length === 0) return null;

  const selectedValue =
    communication_default_email ??
    resolved_communication_default_email ??
    available_emails[0] ??
    "";

  const handleChange = (e: { target: { value: string } }) => {
    setEmailDefault(e.target.value, {
      onSuccess: () => {
        toast.success("Default email updated");
      },
      onError: (error) => {
        const message = axios.isAxiosError<{ message?: string }>(error)
          ? error.response?.data?.message
          : undefined;
        toast.error(message || "Failed to update default email");
      }
    });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-content-600 mb-1">
        Email
      </label>
      {available_emails.length > 1 && type.adminPrivileges ? (
        <Select
          id="property-communication-default-email"
          hideOptional
          value={selectedValue}
          onChange={handleChange}
          options={options}
          isDisabled={isPending}
          legacyErrorMargins={false}
          className="max-w-[280px]"
        />
      ) : (
        <p className="text-sm text-content-400">{selectedValue}</p>
      )}
    </div>
  );
};

export const CommunicationDefaultsSection = ({
  propertyId
}: {
  propertyId: number;
}) => {
  const { accessToken } = useInboxToken();

  // Only fetch phone numbers assigned to this property
  const { data } = usePhoneNumbers({
    enabled: !!accessToken,
    propertyIds: [propertyId]
  });
  const { data: currentDefault } = usePropertyPhoneDefault(
    propertyId,
    !!accessToken
  );
  const { mutate: setPropertyDefault } = useSetPropertyPhoneDefault();

  const activeNumbers = useMemo(
    () => data?.items.filter((pn) => pn.status === "active") ?? [],
    [data]
  );

  const options: BaseOption[] = useMemo(
    () =>
      activeNumbers.map((pn) => ({
        value: pn.communication_service_id,
        label: formatPhone(pn.phone_number ?? "")
      })),
    [activeNumbers]
  );

  const partnerDefaultId = activeNumbers.find(
    (pn) => pn.is_default
  )?.communication_service_id;

  const selectedValue =
    currentDefault?.phone_number_id ||
    partnerDefaultId ||
    activeNumbers[0]?.communication_service_id ||
    "";

  const handleChange = (e: { target: { value: string } }) => {
    setPropertyDefault(
      { propertyId, phoneNumberId: e.target.value },
      {
        onSuccess: () => {
          toast.success("Property phone number default updated");
        },
        onError: (error) => {
          const message = axios.isAxiosError<{ message?: string }>(error)
            ? error.response?.data?.message
            : undefined;
          toast.error(message || "Failed to update default");
        }
      }
    );
  };

  const noNumbersAssigned = data !== undefined && activeNumbers.length === 0;

  return (
    <SectionCard
      title="Communication defaults"
      subtitle="Applicants will receive communications from this entity"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-content-600 mb-1">
            Phone number
          </label>
          <PhoneNumberControl
            propertyId={propertyId}
            activeNumbers={activeNumbers}
            selectedValue={selectedValue}
            options={options}
            noNumbersAssigned={noNumbersAssigned}
            onChange={handleChange}
          />
        </div>
        <EmailDefaultControl />
      </div>
    </SectionCard>
  );
};
